// ============================================
// CONTRIBUTE HOOKS
// Combines hooks from:
// - [ContributeSources.tsx] -> Contribute (CONTRIBUTE SOURCES 270-299)
// - [ContributeSourcesHelpers.tsx] -> [ContributeSources.tsx] -> Contribute (CONTRIBUTE SOURCES 268-279)
// - [ContributeHelpers.tsx] -> Contribute (CONTRIBUTE 291-293)
// - [useContributeAmounts.tsx] -> Contribute (CONTRIBUTE AMOUNTS 254-267, 294-297)
// - [useContributeAmountsHelpers.tsx] -> [useContributeAmounts.tsx] -> Contribute (CONTRIBUTE AMOUNTS 260-267)
// ============================================

import React, {useEffect, useMemo} from 'react';
import {FieldInputProps} from 'formik';
import {floor, merge} from 'lodash';
import moment, {Moment, MomentInput} from 'moment';
import {isSameDay} from 'date-fns';
import {CASH_OBJ, POTS_BOND, POTS_PENSION} from '../../../utils/potsFilter';
import {recommendFieldsType, mainPlanTypeType} from '../CreateRecommend/recommendFields';
import {
  contributeFieldType,
  contributeFromType,
  contributeDestinationValuesType,
  contributeExistingSubplanFields,
} from '../RecommendsContribute/contributeFields';
import type {
  newPlanFields,
  newSubplanFields,
  setUpNewFields,
} from '../RecommendsSetUpNew/setUpNewFields';
import useComboFrequency from '../../../hooks/useComboFrequency';
import {useRecommendContext} from '../CreateRecommend/CreateRecommendHelpers';
import FREQUENCY from '../../../constants/Frequency';
import {ComboFrequencyEnum} from '../../../schema-types/globalTypes';
import {
  useAutoForm,
  useAutoField,
  AutoFieldHelpers,
  SetFieldValueWithSourceKey,
} from '../../../components/AutoForm/useAutoForm';
import usePlanNamesProviders from '../../../hooks/usePlanNamesProviders';
import {useSourcesAndDestinationPots} from '../../../hooks/useSourcesAndDestinationPots';
import {UPDATES} from '../../../shared/UPDATES';
import {OVRecommendation} from '../../../schema-types/OVRecommendation';
import {moneyFieldValueType} from '../../../components/AutoForm/AutoFormNew';
import {Pot} from '../../../schema-types/Pot';
import {getCurrencyFloat} from '../../../utils/currencyFormatter';
import {optionalRound} from '../CreateRecommend/RecommendSourceDestination/utils';
import {getPlanFeesFormikFromServer, defaultFeeFields} from '../CreateRecommend/PlanFees';
import {NEW_PLAN_ID} from '../CreateRecommend/RecommendSourceDestination/constants';
import {includesSaltus} from '../../../utils/includesSaltus';
import {getFeeFormikFromServer} from '../CreateRecommend/DAInitialFees';
import saltus from '../../../shared/logger';
import getMinDate from '../../../utils/getMinDate';
import loadMomentDate from '../../../utils/loadMomentDate';
import {newUTCDate} from '../../../utils/newUTCDate';
import {SelectOption} from '../../../components/AutoForm/AutoFieldNew';

const sortSaltusFirst = <T extends {isSaltus?: boolean | null}>(a: T, b: T) => {
  if (a.isSaltus) return -1;
  if (b.isSaltus) return 1;
  return 0;
};

// ============================================
// ContributeSources.tsx hooks (270-290)
// ============================================

export const setContributeFullAmount = ({
  full,
  maxAmount,
  contributeFromClientHelpers,
}: {
  full: boolean | undefined;
  maxAmount: moneyFieldValueType;
  contributeFromClientHelpers: AutoFieldHelpers<contributeFromType['singleClientAmount']>;
}) => {
  if (full) {
    contributeFromClientHelpers.setValue(UPDATES._288, String(maxAmount));
  }
};

export const calculateNetSingleClient = ({
  singleClientAmount,
  isCash,
}: {
  singleClientAmount: moneyFieldValueType;
  isCash: boolean | undefined | null;
}): number | undefined => {
  try {
    if (singleClientAmount && !isCash) {
      const number = Number(singleClientAmount);
      return floor(number * 0.8, 2);
    }
  } catch (err) {
    // do nothing
  }
  return undefined;
};

export const calculateNetRecurringClient = ({
  recurringClientAmount,
  isCash,
}: {
  recurringClientAmount: moneyFieldValueType;
  isCash: boolean | undefined | null;
}): number | undefined => {
  try {
    if (recurringClientAmount && !isCash) {
      const number = Number(recurringClientAmount);
      return floor(number * 0.8, 2);
    }
  } catch (err) {
    // do nothing
  }
  return undefined;
};

export const deriveIsLisaContribute = ({
  lisaId,
  values,
  planTypeFieldValue,
  sourcesAndDestinations,
  setFieldValue,
  contributeFromHelpers,
  contributeFromFieldObj,
}: {
  lisaId: string | undefined;
  values: recommendFieldsType;
  planTypeFieldValue: string | undefined;
  sourcesAndDestinations: {
    id: string;
    productName?: {formattedName?: string | null} | null;
  }[];
  setFieldValue: SetFieldValueWithSourceKey<recommendFieldsType>;
  contributeFromHelpers: AutoFieldHelpers<contributeFieldType['contributeFrom']>;
  contributeFromFieldObj: {value: contributeFieldType['contributeFrom'] | undefined};
}) => {
  let isLisa = false;
  if (!lisaId || !values.contribute?.contributeTo) {
    isLisa = false;
  } else if (values.contribute?.contributeTo === 'newPlan') {
    isLisa = planTypeFieldValue === lisaId;
  } else {
    isLisa =
      sourcesAndDestinations.find((x) => x.id === values.contribute?.contributeTo)
        ?.productName?.formattedName === 'L-ISA';
  }
  setFieldValue(UPDATES._271, 'isLisa', isLisa);
  if (isLisa) {
    contributeFromHelpers.setValue(
      UPDATES._272,
      {
        ...(contributeFromFieldObj.value as contributeFromType),
        initialFee: undefined,
      },
      true
    );
  }
  return isLisa;
};

export const useSetContributeFullAmount = ({
  contributeFromFieldObj,
  maxAmount,
  contributeFromClientHelpers,
}: {
  contributeFromFieldObj: {value: contributeFieldType['contributeFrom'] | undefined};
  maxAmount: moneyFieldValueType;
  contributeFromClientHelpers: AutoFieldHelpers<contributeFromType['singleClientAmount']>;
}) => {
  useEffect(() => {
    setContributeFullAmount({
      full: contributeFromFieldObj.value?.full,
      maxAmount,
      contributeFromClientHelpers,
    });
  }, [contributeFromFieldObj.value?.full]);
};

export const useCalculateNetSingleClient = ({
  contributeFromFieldObj,
  isCash,
  contributeFromNetHelpers,
}: {
  contributeFromFieldObj: {value: contributeFieldType['contributeFrom'] | undefined};
  isCash: boolean | undefined | null;
  contributeFromNetHelpers: AutoFieldHelpers<contributeFromType['netSingleClientAmount']>;
}) => {
  useEffect(() => {
    const netValue = calculateNetSingleClient({
      singleClientAmount: contributeFromFieldObj?.value?.singleClientAmount,
      isCash,
    });
    if (netValue !== undefined) {
      contributeFromNetHelpers.setValue(UPDATES._278, netValue);
    }
  }, [contributeFromFieldObj?.value?.singleClientAmount, isCash]);
};

export const useCalculateNetRecurringClient = ({
  contributeFromFieldObj,
  isCash,
  contributeFromNetRecurringHelpers,
}: {
  contributeFromFieldObj: {value: contributeFieldType['contributeFrom'] | undefined};
  isCash: boolean | undefined | null;
  contributeFromNetRecurringHelpers: AutoFieldHelpers<
    contributeFromType['netSingleClientAmount']
  >;
}) => {
  useEffect(() => {
    const netValue = calculateNetRecurringClient({
      recurringClientAmount: contributeFromFieldObj?.value?.recurringClientAmount,
      isCash,
    });
    if (netValue !== undefined) {
      contributeFromNetRecurringHelpers.setValue(UPDATES._290, netValue);
    }
  }, [contributeFromFieldObj?.value?.recurringClientAmount, isCash]);
};

export const useDeriveIsLisaContribute = ({
  contributeFromHelpers,
  contributeFromFieldObj,
}: {
  contributeFromHelpers: AutoFieldHelpers<contributeFieldType['contributeFrom']>;
  contributeFromFieldObj: {value: contributeFieldType['contributeFrom'] | undefined};
}) => {
  const {values, setFieldValue} = useAutoForm<recommendFieldsType>();
  const {lisaId, queryGetProducts} = usePlanNamesProviders();
  useEffect(() => {
    if (!lisaId) {
      queryGetProducts({variables: {}});
    }
  }, [lisaId]);
  const [planTypeField] = useAutoField<newPlanFields['planType']>(
    `contribute.newPlan.planType`
  );
  const {sourcesAndDestinations} = useSourcesAndDestinationPots();
  const isLisaDestination = useMemo(() => {
    return deriveIsLisaContribute({
      lisaId,
      values,
      planTypeFieldValue: planTypeField.value,
      sourcesAndDestinations,
      setFieldValue,
      contributeFromHelpers,
      contributeFromFieldObj,
    });
  }, [
    lisaId,
    planTypeField.value,
    values.contribute?.contributeTo,
    sourcesAndDestinations.length,
  ]);
  return isLisaDestination;
};

// ============================================
// useContributeAmounts.tsx (294-297)
// ============================================

// export const useResetContributeAmounts = () => {
//   const {values} = useAutoForm<recommendFieldsType>();
//   const [frequencyComboObj] = useAutoField<contributeFieldType['comboFrequency']>(
//     'contribute.comboFrequency'
//   );
//   const [_, __, contributeFromSingleAmountHelpers] = useAutoField<
//     contributeFromType['singleClientAmount']
//   >('contribute.contributeFrom.singleClientAmount');
//   const [___, ____, contributeFromSingleEmployersAmountHelpers] = useAutoField<
//     contributeFromType['singleEmployerAmount']
//   >('contribute.contributeFrom.singleEmployerAmount');
//   const [_____, ______, contributeFromRecurringAmountHelpers] = useAutoField<
//     contributeFromType['recurringClientAmount']
//   >('contribute.contributeFrom.recurringClientAmount');
//   const [_______, ________, contributeFromRecurringEmployersAmountHelpers] = useAutoField<
//     contributeFromType['recurringEmployerAmount']
//   >('contribute.contributeFrom.recurringEmployerAmount');
//   const {hasSingle, hasRecurring} = useComboFrequency(values.contribute?.comboFrequency);

//   useEffect(() => {
//     if (!hasSingle) {
//       contributeFromSingleEmployersAmountHelpers.setValue(UPDATES._294, undefined, true);
//       contributeFromSingleAmountHelpers.setValue(UPDATES._295, undefined, true);
//     }
//     if (!hasRecurring) {
//       contributeFromRecurringAmountHelpers.setValue(UPDATES._296, undefined, true);
//       contributeFromRecurringEmployersAmountHelpers.setValue(
//         UPDATES._297,
//         undefined,
//         true
//       );
//     }
//   }, [frequencyComboObj.value, hasSingle, hasRecurring]);
// };

// ============================================
// ContributeSourcesHelpers.tsx (268-279, 276-279)
// ============================================

export const setMinimumDateMustBe3WeeksFromToday = ({
  contributeValues,
  setFieldValue,
  setPreviousDate,
  previousDate,
  today, // need to pass this in for testing
}: {
  contributeValues: contributeFieldType | undefined;
  setFieldValue: SetFieldValueWithSourceKey<recommendFieldsType>;
  setPreviousDate: React.Dispatch<React.SetStateAction<string>>;
  previousDate: string;
  today: MomentInput;
}) => {
  if (
    comboToFrequencyMapping[
      contributeValues?.comboFrequency ?? ComboFrequencyEnum.SINGLE
    ] === FREQUENCY.SINGLE ||
    !contributeValues?.contributeFrom
  ) {
    return;
  }
  const minimumDate = getMinDate({
    today,
    dateValue: contributeValues?.contributeFrom?.start as Moment,
    daysOffset: 15,
    isBusiness: true,
  });
  if (minimumDate.date() === 1 || minimumDate.date() === 15) {
    // do nothing
  } else if (minimumDate.date() < 15) {
    minimumDate.set('date', 15);
  } else {
    minimumDate.add(1, 'month');
    minimumDate.startOf('month');
  }
  if (!contributeValues.contributeFrom.start) {
    // upon frequency is anything that isn't single, set start date to 3 weeks in advance if not there
    setFieldValue(UPDATES._268, 'contribute.contributeFrom.start', minimumDate);
    return;
  }

  // this prevents an infinite loop and the value is only set when the date and previous date differ
  if (
    isSameDay(
      moment.utc(contributeValues.contributeFrom?.start).toDate(),
      newUTCDate(previousDate)
    )
  ) {
    return;
  }
  if (moment.utc(contributeValues.contributeFrom?.start).isSame(minimumDate, 'date')) {
    return;
  }
  setFieldValue(UPDATES._269, 'contribute.contributeFrom.start', minimumDate);
  setPreviousDate(minimumDate.toISOString()?.split('T')?.[0] || '');
};

export const updateContributionAmounts = ({
  values,
  destinationValuesHelpers,
  destinationValuesFieldObj,
  contributeFromFieldObj,
}: {
  values: recommendFieldsType;
  contributeFromFieldObj: FieldInputProps<contributeFromType | undefined>;
  destinationValuesHelpers: AutoFieldHelpers<contributeDestinationValuesType>;
  destinationValuesFieldObj: FieldInputProps<contributeDestinationValuesType>;
}) => {
  if (!values.contribute?.contributeTo) {
    return;
  }
  const contributeFrom = contributeFromFieldObj.value;
  const destinationValues: contributeDestinationValuesType = {
    ...destinationValuesFieldObj.value,
    singleClientAmount: optionalRound(contributeFrom?.singleClientAmount) || '',
    recurringClientAmount: optionalRound(contributeFrom?.recurringClientAmount) || '',
    recurringEmployerAmount: optionalRound(contributeFrom?.recurringEmployerAmount) || '',
    singleEmployerAmount: optionalRound(contributeFrom?.singleEmployerAmount) || '',
  };

  destinationValuesHelpers.setValue(UPDATES._278, destinationValues, true);
  const checkedSubplans = Object.entries(
    values.contribute?.destinationValues?.subplans || {}
  ).filter(([id, fields]) => fields.checked);
  const subplans = destinationValuesFieldObj.value?.subplans || {};
  if (checkedSubplans.length) {
    checkedSubplans.forEach(([key], index) => {
      const isFirstSublanWithSecondSublan = index === 0 && checkedSubplans.length > 1;

      const existingSubplan = destinationValuesFieldObj.value?.subplans?.[key];
      const subplan: contributeExistingSubplanFields = {
        planProvider: existingSubplan?.planProvider,
        parentId: existingSubplan?.parentId,
        existingPotId: existingSubplan?.existingPotId ?? null,
        checked: true,
        singleClientAmount:
          optionalRound(
            isFirstSublanWithSecondSublan ? 0 : contributeFrom?.singleClientAmount
          ) || '',
        recurringClientAmount:
          optionalRound(
            isFirstSublanWithSecondSublan ? 0 : contributeFrom?.recurringClientAmount
          ) || '',
        recurringEmployerAmount:
          optionalRound(
            isFirstSublanWithSecondSublan ? 0 : contributeFrom?.recurringEmployerAmount
          ) || '',
        singleEmployerAmount:
          optionalRound(
            isFirstSublanWithSecondSublan ? 0 : contributeFrom?.singleEmployerAmount
          ) || '',
      };
      subplans[key] = subplan;
    });

    destinationValuesHelpers.setValue(
      UPDATES._279,
      {
        ...destinationValuesFieldObj.value,
        subplans,
      },
      true
    );
  }
};

// updateContributionAmounts only syncs amounts to existing-plan destinations.
// For contribute-to-new-plan, subplan0/1 hold model/fees config but no contribution
// amounts — this function mirrors contributeFrom into any checked new-plan subplans.
export const syncContributeNewPlanSubplanAmounts = ({
  contributeFromFieldObj,
  setUpNewFieldObj,
  setUpNewFieldHelpers,
}: {
  contributeFromFieldObj: FieldInputProps<contributeFromType | undefined>;
  setUpNewFieldObj: FieldInputProps<newPlanFields | undefined>;
  setUpNewFieldHelpers: AutoFieldHelpers<newPlanFields | undefined>;
}) => {
  const newPlan = setUpNewFieldObj.value;
  if (!newPlan) return;

  const contributeFrom = contributeFromFieldObj.value;
  const amounts = {
    singleClientAmount: optionalRound(contributeFrom?.singleClientAmount) || '',
    recurringClientAmount: optionalRound(contributeFrom?.recurringClientAmount) || '',
    singleEmployerAmount: optionalRound(contributeFrom?.singleEmployerAmount) || '',
    recurringEmployerAmount: optionalRound(contributeFrom?.recurringEmployerAmount) || '',
  };

  const subplan0 = newPlan.subplan0?.checked
    ? {...newPlan.subplan0, ...amounts}
    : newPlan.subplan0;
  const subplan1 = newPlan.subplan1?.checked
    ? {...newPlan.subplan1, ...amounts}
    : newPlan.subplan1;

  if (subplan0 !== newPlan.subplan0 || subplan1 !== newPlan.subplan1) {
    setUpNewFieldHelpers.setValue(UPDATES._280, {...newPlan, subplan0, subplan1}, true);
  }
};

// ============================================
// ContributeHelpers.tsx (291-293)
// ============================================

const getSelectedSourcePot = (sourceId: string) => (x: Pot) => {
  if (sourceId === CASH_OBJ.id) {
    // if cash, check if cash pot exists
    return x.isDummy && !x.productProvider && !x.productName;
  }
  return x.id === sourceId;
};

export const changeContributeFrom = ({
  contributeFromPots,
  from,
}: {
  contributeFromPots: Pot[];
  from: string;
}): {contributeFrom: contributeFromType; pot: Pot | undefined} => {
  const pot = contributeFromPots.find(getSelectedSourcePot(from));

  return {
    contributeFrom: {
      id: from,
      singleClientAmount: undefined,
      recurringClientAmount: undefined,
      maxAmount: pot && 'totalValue' in pot ? getCurrencyFloat(pot.totalValue) : 0,
      initialFee: null,
      legacyInitialFee: null,
      legacyInitialEmployerFee: null,
      cashPotId: from === CASH_OBJ.id && pot?.id ? pot.id : undefined,
    },
    pot,
  };
};

export const loadContributeFrom = ({
  recommendDetails,
  contributeFromPots,
  from,
  isSPPDA,
}: {
  recommendDetails: OVRecommendation | null;
  contributeFromPots: Pot[];
  from: string;
  isSPPDA: boolean;
}): {contributeFrom: contributeFromType; pot: Pot | undefined} => {
  const selectPot = getSelectedSourcePot(from);
  const ovPot = recommendDetails?.pots?.find((x) => !!x.pot && selectPot(x.pot));
  const potSources = [
    ...(recommendDetails?.pots?.map((x) => x.pot).filter((p) => !!p) || []),
    ...contributeFromPots,
  ];
  const pot = potSources.find(selectPot);
  const initialFee = ovPot?.initialFee?.id || null;
  const legacyInitialFee = getFeeFormikFromServer(isSPPDA, ovPot?.legacyInitialFee);
  const initialEmployerFee = ovPot?.initialEmployerFee?.id || null;
  const legacyInitialEmployerFee = getFeeFormikFromServer(
    isSPPDA,
    ovPot?.legacyInitialEmployerFee
  );
  const sourcePlan = recommendDetails?.pots?.find((p) => p?.planType === 'source');

  return {
    contributeFrom: {
      id: from,
      maxAmount: pot && 'totalValue' in pot ? getCurrencyFloat(pot.totalValue) : 0,
      start: loadMomentDate(recommendDetails?.startDate) as Moment,
      singleClientAmount: optionalRound(sourcePlan?.singleClientAmount) || '',
      recurringClientAmount: optionalRound(sourcePlan?.recurringClientAmount) || '',
      recurringEmployerAmount: optionalRound(sourcePlan?.recurringEmployerAmount) || '',
      singleEmployerAmount: optionalRound(sourcePlan?.singleEmployerAmount) || '',
      inSpecie: sourcePlan?.inSpecie ?? Boolean(sourcePlan?.pot?.inSpecie),
      initialFee,
      initialEmployerFee,
      legacyInitialFee,
      legacyInitialEmployerFee,
      cashPotId: from === CASH_OBJ.id && pot?.id ? pot.id : undefined,
    },
    pot,
  };
};

export const changeContributeFromFactory = ({
  contributeFromPots,
  contributeFromHelpers,
}: {
  contributeFromPots: Pot[];
  contributeFromHelpers: AutoFieldHelpers<contributeFromType | undefined>;
}) => {
  return (event: React.ChangeEvent<any> | null, value: string) => {
    const from = value || event?.target?.value;
    const {contributeFrom} = changeContributeFrom({
      contributeFromPots,
      from,
    });
    contributeFromHelpers.setValue(UPDATES._292, contributeFrom);
  };
};

export const getAllContributePots = ({
  sourcePots,
  destinationPots,
  recommendDetails,
}: {
  sourcePots: Pot[];
  destinationPots: Pot[];
  recommendDetails?: OVRecommendation | null;
}): Pot[] => {
  const allPots = [...destinationPots, ...sourcePots];
  const parentDestinationPot = recommendDetails?.pots?.find(
    (existPots) =>
      existPots.planType === 'destination' && !!existPots.pot && !existPots.pot.parent
  );
  if (parentDestinationPot?.pot) {
    allPots.push(parentDestinationPot.pot);
  }
  return allPots;
};

export const loadContributeNewDestination = ({
  recommendDetails,
  allPots,
}: {
  recommendDetails: OVRecommendation | null;
  allPots: Pot[];
}): {
  destinationValues: contributeDestinationValuesType;
  destinationPot: Pot | undefined;
  newPlan: newPlanFields | undefined;
} => {
  const subplans: Record<string, contributeExistingSubplanFields> = {};
  // if new plan, check if there are any new plans associated with this recc
  const parentDestinationPot = recommendDetails?.pots?.find(
    (existPots) =>
      existPots.planType === 'destination' && !!existPots.pot && !existPots.pot.parent
  );
  const destinationPot = parentDestinationPot
    ? allPots.find((x) => x.id === parentDestinationPot.pot?.id)
    : undefined;

  const planFees = parentDestinationPot
    ? getPlanFeesFormikFromServer(parentDestinationPot)
    : undefined;

  const model = destinationPot?.modelPortfolio || destinationPot?.mandate;
  const newPlan: newPlanFields = {
    contactId: destinationPot?.contact?.id,
    planFees: planFees || defaultFeeFields,
    existingPotId: destinationPot?.id,
    planProvider: destinationPot?.productProvider?.id || undefined,
    inTrust: destinationPot?.inTrust || undefined,
    planType: destinationPot?.productName?.id || undefined,
    model: model || undefined,
    subplan0: undefined,
    subplan1: undefined,
    parentGuardianId: recommendDetails?.parentGuardian?.id,
  };

  const childPots =
    destinationPot?.children?.filter((x) =>
      x.productName?.acronym?.toLocaleLowerCase().includes('giagross')
    ) || [];
  if (childPots.length) {
    [...childPots].sort(sortSaltusFirst).forEach((_childPot, index) => {
      const ovPotInRecc = recommendDetails?.pots?.find((x) => x.pot?.id === _childPot.id);
      const potForModel = ovPotInRecc?.pot || _childPot;
      const subplanFees = getPlanFeesFormikFromServer(ovPotInRecc);
      const subplanModel = potForModel?.modelPortfolio || potForModel?.mandate;

      const subplanValues: contributeExistingSubplanFields = {
        existingPotId: potForModel?.id || null,
        parentId: destinationPot!.id,
        checked: !!ovPotInRecc || childPots?.length === 1,
        recurringClientAmount: ovPotInRecc?.recurringClientAmount || '',
        recurringEmployerAmount: ovPotInRecc?.recurringEmployerAmount || '',
        singleClientAmount: ovPotInRecc?.singleClientAmount || '',
        singleEmployerAmount: ovPotInRecc?.singleEmployerAmount || '',
      };
      const subplanNewValues: newSubplanFields = merge({}, subplanValues, {
        model: subplanModel || undefined,
        planProvider: potForModel?.productProvider?.id || undefined,
        productProviderId: potForModel?.productProvider?.id || undefined,
        planFees: subplanFees,
      });
      if (index === 0) {
        newPlan.subplan0 = subplanNewValues;
      } else {
        newPlan.subplan1 = subplanNewValues;
      }
      subplans[_childPot.id] = subplanValues;
    });
  }

  return {
    destinationValues: {
      planProvider: destinationPot?.productProvider?.id,
      subplans,
      singleClientAmount: optionalRound(parentDestinationPot?.singleClientAmount),
      singleEmployerAmount: optionalRound(parentDestinationPot?.singleEmployerAmount),
      recurringEmployerAmount: optionalRound(
        parentDestinationPot?.recurringEmployerAmount
      ),
      recurringClientAmount: optionalRound(parentDestinationPot?.recurringClientAmount),
      isSaltusProvider: includesSaltus(destinationPot?.productProvider?.name),
    },
    destinationPot,
    newPlan,
  };
};

export const changeContributeNewDestination = ({
  allPots,
  contributeValues,
  contributeFromValues,
}: {
  allPots: Pot[];
  contributeValues: contributeFieldType | undefined;
  contributeFromValues: contributeFromType | undefined;
}): {
  destinationValues: contributeDestinationValuesType;
  destinationPot: Pot | undefined;
  newPlan: newPlanFields | undefined;
} => {
  const subplans: Record<string, contributeExistingSubplanFields> = {};

  const newPlan: newPlanFields = contributeValues?.newPlan || {
    contactId: '',
    planFees: defaultFeeFields,
    planProvider: '',
    planType: '',
  };

  const destinationPot = contributeValues?.newPlan?.existingPotId
    ? allPots.find((x) => x.id === contributeValues?.newPlan?.existingPotId)
    : undefined;

  return {
    destinationValues: {
      planProvider: destinationPot?.productProvider?.id,
      subplans,
      singleClientAmount: optionalRound(
        contributeValues?.destinationValues?.singleClientAmount ||
          contributeFromValues?.singleClientAmount
      ),
      singleEmployerAmount: optionalRound(
        contributeValues?.destinationValues?.singleEmployerAmount ||
          contributeFromValues?.singleEmployerAmount
      ),
      recurringEmployerAmount: optionalRound(
        contributeValues?.destinationValues?.recurringEmployerAmount ||
          contributeFromValues?.recurringEmployerAmount
      ),
      recurringClientAmount: optionalRound(
        contributeValues?.destinationValues?.recurringClientAmount ||
          contributeFromValues?.recurringClientAmount
      ),
      isSaltusProvider: includesSaltus(destinationPot?.productProvider?.name),
    },
    destinationPot,
    newPlan,
  };
};

export const loadContributeExistingDestination = ({
  recommendDetails,
  allPots,
  to,
}: {
  recommendDetails: OVRecommendation | null;
  allPots: Pot[];
  to: string;
}): {
  destinationValues: contributeDestinationValuesType;
  destinationPot: Pot | undefined;
} => {
  const subplans: Record<string, contributeExistingSubplanFields> = {};
  const parentDestinationPot = recommendDetails?.pots?.find(
    (existPots) =>
      existPots.planType === 'destination' && !!existPots.pot && !existPots.pot.parent
  );
  const destinationPot = allPots.find((x) => x.id === to);

  const childPots =
    destinationPot?.children?.filter((x) =>
      x.productName?.acronym?.toLocaleLowerCase().includes('giagross')
    ) || [];
  if (childPots.length) {
    [...childPots].sort(sortSaltusFirst).forEach((_childPot) => {
      const ovPotInRecc = recommendDetails?.pots?.find((x) => x.pot?.id === _childPot.id);
      const potForModel = ovPotInRecc?.pot || _childPot;

      const subplanValues: contributeExistingSubplanFields = {
        existingPotId: potForModel?.id || null,
        parentId: destinationPot!.id,
        checked: !!ovPotInRecc || childPots?.length === 1,
        recurringClientAmount: ovPotInRecc?.recurringClientAmount || '',
        recurringEmployerAmount: ovPotInRecc?.recurringEmployerAmount || '',
        singleClientAmount: ovPotInRecc?.singleClientAmount || '',
        singleEmployerAmount: ovPotInRecc?.singleEmployerAmount || '',
      };
      subplans[_childPot.id] = subplanValues;
    });
  }

  return {
    destinationValues: {
      planProvider: destinationPot?.productProvider?.id,
      subplans,
      singleClientAmount: optionalRound(parentDestinationPot?.singleClientAmount),
      singleEmployerAmount: optionalRound(parentDestinationPot?.singleEmployerAmount),
      recurringEmployerAmount: optionalRound(
        parentDestinationPot?.recurringEmployerAmount
      ),
      recurringClientAmount: optionalRound(parentDestinationPot?.recurringClientAmount),
      isSaltusProvider: includesSaltus(destinationPot?.productProvider?.name),
    },
    destinationPot,
  };
};

export const changeContributeExistingPlan = ({
  allPots,
  to,
  contributeValues,
  contributeFromValues,
}: {
  allPots: Pot[];
  to: string;
  contributeValues: contributeFieldType | undefined;
  contributeFromValues: contributeFromType | undefined;
}): {
  destinationValues: contributeDestinationValuesType;
  destinationPot: Pot | undefined;
} => {
  const subplans: Record<string, contributeExistingSubplanFields> = {};
  const destinationPot = allPots.find((x) => x.id === to);

  const childPots =
    destinationPot?.children?.filter((x) =>
      x.productName?.acronym?.toLocaleLowerCase().includes('giagross')
    ) || [];
  if (childPots.length) {
    [...childPots].sort(sortSaltusFirst).forEach((_childPot) => {
      const subplanValues: contributeExistingSubplanFields = {
        existingPotId: _childPot?.id || null,
        parentId: destinationPot!.id,
        checked: childPots?.length === 1,
        recurringClientAmount: '',
        recurringEmployerAmount: '',
        singleClientAmount: '',
        singleEmployerAmount: '',
      };
      subplans[_childPot.id] = subplanValues;
    });
  }

  return {
    destinationValues: {
      planProvider: destinationPot?.productProvider?.id,
      subplans,
      singleClientAmount: optionalRound(
        contributeValues?.destinationValues?.singleClientAmount ||
          contributeFromValues?.singleClientAmount
      ),
      singleEmployerAmount: optionalRound(
        contributeValues?.destinationValues?.singleEmployerAmount ||
          contributeFromValues?.singleEmployerAmount
      ),
      recurringEmployerAmount: optionalRound(
        contributeValues?.destinationValues?.recurringEmployerAmount ||
          contributeFromValues?.recurringEmployerAmount
      ),
      recurringClientAmount: optionalRound(
        contributeValues?.destinationValues?.recurringClientAmount ||
          contributeFromValues?.recurringClientAmount
      ),
      isSaltusProvider: includesSaltus(destinationPot?.productProvider?.name),
    },
    destinationPot,
  };
};

export const changeContributeDestination = ({
  allPots,
  to,
  contributeValues,
  contributeFromValues,
}: {
  allPots: Pot[];
  to: string;
  contributeValues: contributeFieldType | undefined;
  contributeFromValues: contributeFromType | undefined;
}): contributeFieldType => {
  const isNewPlan = to === NEW_PLAN_ID;

  let destinationValues: contributeDestinationValuesType;
  let destinationPot: Pot | undefined;
  let newPlan: newPlanFields | undefined;

  if (isNewPlan) {
    const result = changeContributeNewDestination({
      allPots,
      contributeValues,
      contributeFromValues,
    });
    destinationValues = result.destinationValues;
    destinationPot = result.destinationPot;
    newPlan = result.newPlan;
  } else {
    const result = changeContributeExistingPlan({
      allPots,
      to,
      contributeValues,
      contributeFromValues,
    });
    destinationValues = result.destinationValues;
    destinationPot = result.destinationPot;
  }

  const returnVal = {
    comboFrequency: contributeValues?.comboFrequency ?? ComboFrequencyEnum.SINGLE,
    contributeFrom: contributeValues?.contributeFrom,
    contributeTo: to,
    planProvider: destinationPot?.productProvider?.id,
    destinationValues,
    newPlan,
  };
  console.log(`changeContributeDestination returnVal ${JSON.stringify(returnVal)}`);
  return returnVal;
};

export const changeContributeToFactory = ({
  allPots,
  contributeFieldObj,
  contributeFromObj,
  contributeHelpers,
}: {
  allPots: Pot[];
  contributeFieldObj: FieldInputProps<contributeFieldType | undefined>;
  contributeFromObj: FieldInputProps<contributeFromType | undefined>;
  contributeHelpers: AutoFieldHelpers<contributeFieldType | undefined>;
}) => {
  return (event: React.ChangeEvent<any> | null, value: string) => {
    const to = value || event?.target?.value;
    const newContribute = changeContributeDestination({
      allPots,
      to,
      contributeValues: contributeFieldObj.value,
      contributeFromValues: contributeFromObj.value,
    });
    console.log(`changeContributeToFactory newContribute`, newContribute);
    contributeHelpers.setValue(UPDATES._293, newContribute);
  };
};

export const loadSavedContribution = ({
  recommendDetails,
  allPots,
  isSPPDA,
}: {
  recommendDetails: OVRecommendation | null;
  allPots: Pot[];
  isSPPDA: boolean;
}): contributeFieldType => {
  const {comboFrequency} = getContributionFrequencies(recommendDetails);

  if (!recommendDetails) {
    return {
      comboFrequency: ComboFrequencyEnum.SINGLE, // default to single
      destinationValues: {
        recurringClientAmount: 0,
        recurringEmployerAmount: 0,
        singleClientAmount: 0,
        singleEmployerAmount: 0,
      },
      contributeTo: '',
      planProvider: '',
    };
  }

  const toPot = recommendDetails?.pots?.find(
    (p) => p?.planType === 'destination' && !!p.pot && !p.pot.parent
  );
  const to =
    toPot?.pot?.isDummy && toPot.pot.originalOvrId === recommendDetails.id
      ? NEW_PLAN_ID
      : toPot?.pot?.id;

  const isNewPlan = to === NEW_PLAN_ID;

  let destinationValues: contributeDestinationValuesType;
  let destinationPot: Pot | undefined;
  let newPlan: newPlanFields | undefined;

  if (isNewPlan) {
    const result = loadContributeNewDestination({
      recommendDetails,
      allPots,
    });
    destinationValues = result.destinationValues;
    destinationPot = result.destinationPot;
    newPlan = result.newPlan;
  } else {
    const result = loadContributeExistingDestination({
      recommendDetails,
      allPots,
      to,
    });
    destinationValues = result.destinationValues;
    destinationPot = result.destinationPot;
  }

  const sourcePlan = recommendDetails?.pots?.find((p) => p?.planType === 'source');
  const from =
    sourcePlan?.pot?.isCash || (sourcePlan?.pot?.isDummy && !sourcePlan.pot.productName)
      ? CASH_OBJ.id
      : sourcePlan?.pot?.id;

  const {contributeFrom} = loadContributeFrom({
    recommendDetails,
    contributeFromPots: allPots,
    from,
    isSPPDA,
  });

  const newContribute: contributeFieldType = {
    comboFrequency,
    contributeTo: to,
    planProvider: destinationPot?.productProvider?.id,
    destinationValues,
    newPlan,
    contributeFrom,
  };
  saltus.log({newContribute});
  return newContribute;
};

// ============================================
// CONTRIBUTE FREQUENCY HELPERS
// ============================================

export const comboToFrequencyMapping: Record<ComboFrequencyEnum, FREQUENCY> = {
  [ComboFrequencyEnum.ANNUAL]: FREQUENCY.ANNUAL,
  [ComboFrequencyEnum.ANNUAL_AND_SINGLE]: FREQUENCY.ANNUAL,
  [ComboFrequencyEnum.MONTHLY]: FREQUENCY.MONTHLY,
  [ComboFrequencyEnum.MONTHLY_AND_SINGLE]: FREQUENCY.MONTHLY,
  [ComboFrequencyEnum.QUARTERLY]: FREQUENCY.QUARTERLY,
  [ComboFrequencyEnum.QUARTERLY_AND_SINGLE]: FREQUENCY.QUARTERLY,
  [ComboFrequencyEnum.SINGLE]: FREQUENCY.SINGLE,
};

const frequencyWithSingleToCombo: Record<FREQUENCY, ComboFrequencyEnum> = {
  [FREQUENCY.ANNUAL]: ComboFrequencyEnum.ANNUAL_AND_SINGLE,
  [FREQUENCY.MONTHLY]: ComboFrequencyEnum.MONTHLY_AND_SINGLE,
  [FREQUENCY.QUARTERLY]: ComboFrequencyEnum.QUARTERLY_AND_SINGLE,
  [FREQUENCY.SINGLE]: ComboFrequencyEnum.SINGLE,
};

const recurringFrequencyToCombo: Record<FREQUENCY, ComboFrequencyEnum> = {
  [FREQUENCY.ANNUAL]: ComboFrequencyEnum.ANNUAL,
  [FREQUENCY.MONTHLY]: ComboFrequencyEnum.MONTHLY,
  [FREQUENCY.QUARTERLY]: ComboFrequencyEnum.QUARTERLY,
  [FREQUENCY.SINGLE]: ComboFrequencyEnum.SINGLE,
};

export const getContributionFrequencies = (
  recommendDetails: OVRecommendation | null
): {comboFrequency: ComboFrequencyEnum; frequency: FREQUENCY} => {
  if (!recommendDetails || !recommendDetails.action) {
    return {comboFrequency: ComboFrequencyEnum.SINGLE, frequency: FREQUENCY.SINGLE};
  }
  const potWithNotSingleFrequency = recommendDetails.pots?.find(
    (p) =>
      (p.recurringClientFrequency && p.recurringClientFrequency !== FREQUENCY.SINGLE) ||
      (p.recurringEmployerFrequency && p.recurringEmployerFrequency !== FREQUENCY.SINGLE)
  );
  const recurringFrequency = potWithNotSingleFrequency?.recurringClientFrequency as
    | FREQUENCY
    | undefined;

  const alsoIncludeSingle = recommendDetails.pots?.find(
    (p) =>
      (p.singleClientAmount && Number(p.singleClientAmount) > 0) ||
      (p.singleEmployerAmount && Number(p.singleEmployerAmount) > 0)
  );

  if (recurringFrequency) {
    const comboFrequency = alsoIncludeSingle
      ? frequencyWithSingleToCombo[recurringFrequency]
      : recurringFrequencyToCombo[recurringFrequency];
    const frequency = comboToFrequencyMapping[comboFrequency];
    return {comboFrequency, frequency};
  }

  return {comboFrequency: ComboFrequencyEnum.SINGLE, frequency: FREQUENCY.SINGLE};
};
